import React, { useState, useEffect, useRef } from "react";
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
} from "chart.js";
import { Bar } from "react-chartjs-2";
import Box from "@mui/material/Box";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import { Popover, Button } from "@mui/material";
import { format } from "date-fns";
import { DateRange } from "react-date-range";
import { useQuery } from "@tanstack/react-query";
import { GetChatMessagesChart } from "../../../Api/analytics";
import { ErrorToast } from "../../../Utils/SweetAlert";

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const ChatMessageChart = () => {
    const [analyticsPeriod, setAnalyticsPeriod] = useState("month");
    const [userType, setUserType] = useState("all");
    const [anchorEl, setAnchorEl] = useState(null);
    const selectRef = useRef(null);
    const [dateRange, setDateRange] = useState([
        {
            startDate: new Date(),
            endDate: new Date(),
            key: "selection",
        },
    ]);
    const [tempDateRange, setTempDateRange] = useState([
        {
            startDate: new Date(),
            endDate: new Date(),
            key: "selection",
        },
    ]);

    const { data: chartData, isFetching: loading, refetch } = useQuery({
        queryKey: ["get-chat-messages-chart", analyticsPeriod, userType, dateRange],
        queryFn: () => {
            let startDate = "";
            let endDate = "";
            if (analyticsPeriod === "custom") {
                startDate = format(dateRange[0].startDate, "yyyy-MM-dd");
                endDate = format(dateRange[0].endDate, "yyyy-MM-dd");
            }
            return GetChatMessagesChart({
                type: analyticsPeriod,
                userType: userType === "all" ? "" : userType,
                startDate,
                endDate
            });
        },
        select: (response) => {
            const labels = response?.data?.dateWise?.map(item => item.label) || [];
            const values = response?.data?.dateWise?.map(item => item.count) || [];
            return {
                labels,
                datasets: [
                    {
                        label: "Messages",
                        data: values,
                        backgroundColor: "#AD5ADC",
                    },
                ],
            };
        },
        throwOnError: (error) => {
            ErrorToast(error?.response?.data?.message || "Server Error.");
        },
    });

    const options = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: { position: "bottom", labels: { font: { size: 14 } } },
            title: { display: false },
        },
        scales: {
            x: { ticks: { font: { size: 12 } } },
            y: { ticks: { font: { size: 12 } } },
        },
    };

    const handleChange = (event) => {
        const value = event.target.value;
        if (value === "custom") {
            setAnchorEl(selectRef.current);
            setAnalyticsPeriod("custom");
            setTempDateRange(dateRange);
        } else {
            setAnalyticsPeriod(value);
            setAnchorEl(null);
        }
    };

    const handleUserTypeChange = (event) => {
        setUserType(event.target.value);
    };

    const handleDateSelect = (ranges) => {
        setTempDateRange([ranges.selection]);
    };

    const handleApplyCustomDate = () => {
        setDateRange(tempDateRange);
        setAnchorEl(null);
    };

    const handleClosePopover = () => setAnchorEl(null);
    const open = Boolean(anchorEl);
    const id = open ? "simple-popover" : undefined;

    useEffect(() => {
        refetch();
    }, [analyticsPeriod, userType, dateRange, refetch]);

    return (
        <div className="card flex-fill border-card mb-3">
            <div className="card-header dashboard-chat-heading p-2">
                <div className="d-flex justify-content-between align-items-center">
                    <h5 className="card-title text-black fw-bold fs-4 pl-2">Chat Message Overview</h5>
                    <div className="d-flex align-items-center gap-2">
                        <Box sx={{ minWidth: 150 }}>
                            <FormControl fullWidth>
                                <Select
                                    className="fw-bold border border-2 border-white"
                                    style={{ color: "black", borderRadius: "15px" }}
                                    value={userType}
                                    onChange={handleUserTypeChange}
                                >
                                    <MenuItem value="all">All Users</MenuItem>
                                    <MenuItem value="freeUser">Free User</MenuItem>
                                    <MenuItem value="paidUser">Paid User</MenuItem>
                                </Select>
                            </FormControl>
                        </Box>
                        {analyticsPeriod === "custom" && (
                            <span
                                className="me-2 fw-bold"
                                style={{ color: "black", cursor: "pointer" }}
                                onClick={() => setAnchorEl(selectRef.current)}
                            >
                                {format(dateRange[0].startDate, "dd/MM/yyyy")} - {format(dateRange[0].endDate, "dd/MM/yyyy")}
                            </span>
                        )}
                        <Box sx={{ minWidth: 190 }}>
                            <FormControl fullWidth ref={selectRef}>
                                <Select
                                    className="fw-bold border border-2 border-white"
                                    style={{ color: "black", borderRadius: "15px" }}
                                    value={analyticsPeriod}
                                    onChange={handleChange}
                                >
                                    <MenuItem value="today">Today</MenuItem>
                                    <MenuItem value="week">This Week</MenuItem>
                                    <MenuItem value="last_week">Last Week</MenuItem>
                                    <MenuItem value="month">Monthly</MenuItem>
                                    <MenuItem value="last_12_months">Last 12 Months</MenuItem>
                                    <MenuItem value="year">Yearly</MenuItem>
                                    <MenuItem value="custom">Custom Dates</MenuItem>
                                </Select>
                                <Popover
                                    id={id}
                                    open={open}
                                    anchorEl={anchorEl}
                                    onClose={handleClosePopover}
                                    anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                                    transformOrigin={{ vertical: "top", horizontal: "right" }}
                                >
                                    <Box
                                        p={2}
                                        sx={{
                                            width: 'fit-content',
                                            '& .rdrDefinedRangesWrapper': { display: 'none' },
                                        }}
                                        display="flex"
                                        flexDirection="column"
                                        alignItems="flex-end"
                                    >
                                        <DateRange
                                            editableDateInputs={true}
                                            onChange={handleDateSelect}
                                            moveRangeOnFirstSelection={false}
                                            ranges={tempDateRange}
                                            months={1}
                                            rangeColors={['#AD5ADC']}
                                            showDateDisplay={false}
                                            maxDate={new Date()}
                                        />
                                        <Button
                                            variant="contained"
                                            onClick={handleApplyCustomDate}
                                            sx={{
                                                mt: 2,
                                                backgroundColor: "#AD5ADC",
                                                "&:hover": { backgroundColor: "#8E44AD" },
                                                borderRadius: '15px'
                                            }}
                                        >
                                            Apply
                                        </Button>
                                    </Box>
                                </Popover>
                            </FormControl>
                        </Box>
                    </div>
                </div>
            </div>
            <div className="card-body" style={{ height: '60vh' }}>
                {loading ? (
                    <div className="d-flex justify-content-center align-items-center h-100">
                        <div className="spinner-border loader-theme" role="status" />
                    </div>
                ) : (
                    <Bar options={options} data={chartData} />
                )}
            </div>
        </div>
    );
};

export default ChatMessageChart;
